import { GetStaticProps } from 'next';
import { GetStaticPropsContext } from 'next/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import MainSeo from '@components/MainSeo/MainSeo';
import SelfCanonicalLink from '@features/seo/SelfCanonicalLink/SelfCanonicalLink';
import { getLayoutData } from '@features/layout/api';
import { getReturnsCmsDocumentBySlug } from '@features/cms/services/CmsServices';
import ReturnsPageContent from '@features/returns/components/ReturnsPageContent/ReturnsPageContent';
import { extractAccordionBlock, extractSmallHeaderBlock, RETURNS_FAQ_CMS_SLUG } from '@features/returns/utils/extractCmsBlocks';
import { getDevProps } from '@components/DevToolbar/helpers/getDevProps';
import { REVALIDATION_TIME } from '@config/index';
import { ISeo, TLocale } from '@typescript/index';
import { TReturnsContent } from '@features/returns/utils/extractCmsBlocks';
import { IAccordion } from '@features/pages/typescript';
import { removeUndefined } from '@utils/removeUndefined';
import { trackRevalidation } from '@utils/revalidationTracker';
import logger from '@utils/logger';
type PropsType = {
  seo?: ISeo;
  headerContent?: TReturnsContent | null;
  accordionBlock?: IAccordion | null;
  isBuildTime?: boolean;
};
const ReturnsPage = ({
  seo,
  headerContent,
  accordionBlock,
  isBuildTime
}: PropsType) => {
  if (isBuildTime) {
    return <div>Build time</div>;
  }
  return <>
      <SelfCanonicalLink />
      <main id="main-content">
        {seo && <MainSeo seo={seo} />}
        <ReturnsPageContent headerContent={headerContent} accordionBlock={accordionBlock} />
      </main>
    </>;
};
export const getStaticProps: GetStaticProps = async ({
  locale
}: GetStaticPropsContext) => {
  if (process.env.IS_BUILD) {
    return {
      props: {
        isBuildTime: true
      },
      revalidate: 1
    };
  }
  const document = await getReturnsCmsDocumentBySlug(locale as TLocale, RETURNS_FAQ_CMS_SLUG);
  const faqDocument = document ? document.children?.find(child => child.baseData?.behaviour === 'groupAccordion') : null;
  const content = document ? await document.getAdaptedContent() : null;
  const faqContent = faqDocument ? await faqDocument.getAdaptedContent() : null;
  const headerContent = extractSmallHeaderBlock(content);
  const accordionBlock = extractAccordionBlock(faqContent) ?? extractAccordionBlock(content);
  if (document?.seo) {
    document.seo.imagePaths = document.getAllImagePaths();
  }
  const layoutData = await getLayoutData(locale as TLocale);
  try {
    await trackRevalidation('zwroty', locale as string, REVALIDATION_TIME, 'static', 'pages');
  } catch (error) {
    logger.error('Failed to track revalidation:', error);
  }
  return {
    props: {
      ...getDevProps('static', REVALIDATION_TIME),
      seo: document?.seo ? removeUndefined(document.seo) : null,
      headerContent: headerContent ? removeUndefined(headerContent) : null,
      accordionBlock: accordionBlock ? removeUndefined(accordionBlock) : null,
      isBuildTime: false,
      ...layoutData,
      ...(await serverSideTranslations(locale as string))
    },
    revalidate: REVALIDATION_TIME
  };
};
export default ReturnsPage;